import { useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useCurrentUser } from '../services/userHooks';
import type { LayoutConfig } from '../types/layout';
import type { User } from '../types/api';

interface RouteLayoutConfig {
  [routePattern: string]: LayoutConfig;
}

// Default layout configurations for different routes
const DEFAULT_ROUTE_CONFIGS: RouteLayoutConfig = {
  // App routes - require authentication.
  // NOTE: "/" is the standalone public landing page (renders its own chrome,
  // not via LayoutSelector); this map is only consulted as a fallback when a
  // LayoutSelector caller omits an explicit config, which no route currently
  // does. "/home" is the authenticated bookmark list.
  '/': { type: 'app', requiresAuth: true },
  '/home': { type: 'app', requiresAuth: true },
  '/archive': { type: 'app', requiresAuth: true },
  '/liked': { type: 'app', requiresAuth: true },
  '/videos': { type: 'app', requiresAuth: true },
  '/search': { type: 'app', requiresAuth: true },
  '/notes': { type: 'app', requiresAuth: true },
  '/folder/*': { type: 'app', requiresAuth: true },
  '/tags/*': { type: 'app', requiresAuth: true },
  '/read/*': { type: 'app', requiresAuth: true },
  '/p/*': { type: 'app', requiresAuth: true },

  // Settings/profile routes - hybrid (can work with or without auth)
  '/settings': { type: 'hybrid', requiresAuth: false, showAuthButtons: true },
  '/settings/*': { type: 'hybrid', requiresAuth: false, showAuthButtons: true },

  // Docs routes - publicly accessible
  '/docs': { type: 'hybrid', requiresAuth: false, showAuthButtons: true },
  '/docs/*': { type: 'hybrid', requiresAuth: false, showAuthButtons: true },

  // Guest routes - no authentication required
  '/user/login': { type: 'guest', requiresAuth: false, showAuthButtons: false },
  '/user/register': { type: 'guest', requiresAuth: false, showAuthButtons: false },
  '/premium': { type: 'guest', requiresAuth: false, showAuthButtons: true },
  '/privacy': { type: 'guest', requiresAuth: false, showAuthButtons: true },
  '/tos': { type: 'guest', requiresAuth: false, showAuthButtons: true },
  '/help': { type: 'guest', requiresAuth: false, showAuthButtons: true },
  '/iphone': { type: 'guest', requiresAuth: false, showAuthButtons: true },
  '/android': { type: 'guest', requiresAuth: false, showAuthButtons: true },
};

/**
 * Simple pattern matching for routes with wildcards
 */
function matchesPattern(pathname: string, pattern: string): boolean {
  if (!pattern.includes('*')) {
    return pathname === pattern;
  }

  const regexPattern = pattern
    .replace(/\*/g, '[^/]+') // * matches any characters except /
    .replace(/\//g, '\\/'); // escape forward slashes

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(pathname);
}

/**
 * Get layout configuration for a specific route
 */
function getRouteConfig(pathname: string): LayoutConfig {
  // Exact match first
  if (DEFAULT_ROUTE_CONFIGS[pathname]) {
    return DEFAULT_ROUTE_CONFIGS[pathname];
  }

  // Pattern matching for dynamic routes
  for (const [pattern, config] of Object.entries(DEFAULT_ROUTE_CONFIGS)) {
    if (matchesPattern(pathname, pattern)) {
      return config;
    }
  }

  // Default to app layout for unknown routes
  return { type: 'app', requiresAuth: true };
}

/**
 * Determines the appropriate layout type based on current route and user state
 */
function determineLayout(
  pathname: string,
  user: User | null | undefined,
  customConfig?: LayoutConfig,
): LayoutConfig {
  // Use custom config if provided
  if (customConfig) {
    return customConfig;
  }

  // Find matching route configuration
  const config = getRouteConfig(pathname);

  // Handle hybrid layouts that adapt to auth state
  if (config.type === 'hybrid') {
    return {
      ...config,
      // Show different content based on auth state but same layout structure
      type: 'hybrid',
    };
  }

  // For auth-required routes, redirect to guest if no user
  if (config.requiresAuth && !user) {
    return {
      type: 'guest',
      requiresAuth: false,
      showAuthButtons: true,
    };
  }

  return config;
}

export interface UseLayoutConfigOptions {
  customConfig?: LayoutConfig;
}

export interface UseLayoutConfigReturn {
  layoutConfig: LayoutConfig;
  user: User | null | undefined;
  isLoading: boolean;
  pathname: string;
  requiresAuth: boolean;
  shouldShowAuthButtons: boolean;
}

/**
 * Hook that provides layout configuration based on current route and user state
 */
export function useLayoutConfig(
  options: UseLayoutConfigOptions = {},
): UseLayoutConfigReturn {
  // Handle case where router is not available (e.g., in tests)
  let pathname = '/';
  try {
    const location = useLocation();
    pathname = location.pathname;
  } catch {
    // Fallback to default pathname if not in router context
    pathname = '/';
  }

  const { currentUser: user, isLoading, error } = useCurrentUser();

  // If there's an error (likely no auth), treat as no user
  const currentUser = error ? null : user;

  const layoutConfig = useMemo(() => {
    return determineLayout(pathname, currentUser, options.customConfig);
  }, [pathname, currentUser, options.customConfig]);

  const requiresAuth = useMemo(() => {
    const config = getRouteConfig(pathname);
    return config.requiresAuth ?? true;
  }, [pathname]);

  const shouldShowAuthButtons = useMemo(() => {
    const config = determineLayout(pathname, currentUser);
    return config.showAuthButtons ?? true;
  }, [pathname, currentUser]);

  return {
    layoutConfig,
    user: currentUser,
    isLoading,
    pathname,
    requiresAuth,
    shouldShowAuthButtons,
  };
}
